import { register, run } from "./registry";
import { moveFocus, focusField } from "./keyboard";

let referenceListId = 0;

/** 06 §1 grid keys + 06 §2 C.N. auto-fill. */
register((root) => {
  root.querySelectorAll<HTMLTableElement>("table[data-kit-grid]").forEach((grid) => {
    if (grid.dataset.kitBound === "1") return;
    grid.dataset.kitBound = "1";
    const tpl = grid.querySelector<HTMLTemplateElement>("template[data-kit-row]");
    const body = grid.tBodies[0] ?? grid.createTBody();
    type BufferedKey = { key: string; shiftKey: boolean; ctrlKey?: boolean };
    type SeriesHit = { id: string; name: string };
    type LookupResponse = Record<string, unknown> & {
      ok?: boolean;
      message?: string;
      eligibility?: { ok: boolean; rule?: string; level?: string; message?: string };
    };
    const pendingLookups = new WeakMap<HTMLInputElement, BufferedKey[]>();
    const replay = (keys: BufferedKey[]) => {
      const selectSearch = new Map<HTMLSelectElement, string>();
      for (const action of keys) {
        const field = document.activeElement;
        // A held save goes wherever focus now is (e.g. Remarks after leaving the grid), so it is never lost.
        if (action.key === "F10" || (action.ctrlKey && action.key.toLowerCase() === "s")) {
          (field ?? document.body).dispatchEvent(new KeyboardEvent("keydown", { ...action, bubbles: true, cancelable: true }));
          continue;
        }
        if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) break;
        if (!field.dispatchEvent(new KeyboardEvent("keydown", { ...action, bubbles: true, cancelable: true }))) continue;
        if (field instanceof HTMLSelectElement && action.key.length === 1) {
          const search = (selectSearch.get(field) ?? "") + action.key.toLowerCase();
          selectSearch.set(field, search);
          const option = Array.from(field.options).find(o => o.text.toLowerCase().startsWith(search));
          if (option) field.value = option.value;
        } else if (field instanceof HTMLInputElement && !field.readOnly) {
          const start = field.selectionStart ?? field.value.length;
          const end = field.selectionEnd ?? start;
          if (action.key.length === 1) {
            if (field.selectionStart === null) field.value += action.key;
            else field.setRangeText(action.key, start, end, "end");
          } else if (action.key === "Backspace" || action.key === "Delete") {
            const from = start === end && action.key === "Backspace" ? Math.max(0, start - 1) : start;
            const to = start === end && action.key === "Delete" ? Math.min(field.value.length, end + 1) : end;
            if (field.selectionStart === null) field.value = field.value.slice(0, -1);
            else field.setRangeText("", from, to, "end");
          } else continue;
        } else continue;
        field.dispatchEvent(new Event("input", { bubbles: true }));
      }
    };
    const cells = (tr: HTMLTableRowElement) =>
      Array.from(tr.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input[data-kit-col], select[data-kit-col], input:not([type='hidden']), select"))
        .filter((c) => !(c instanceof HTMLInputElement && (c.readOnly || c.type === "hidden")) && !c.disabled && c.getAttribute("tabindex") !== "-1"); // tabindex -1: shown, not an Enter stop

    const updateSums = () => {
      body.querySelectorAll("tr").forEach((row) => {
        const amtInput = row.querySelector<HTMLInputElement>("[data-kit-col='amount']");
        const rebateInput = row.querySelector<HTMLInputElement>("[data-kit-col='rebate']");
        const otherInput = row.querySelector<HTMLInputElement>("[data-kit-col='otherCharge']");
        const netInput = row.querySelector<HTMLInputElement>("[data-kit-col='net']");
        if (amtInput && netInput) {
          const a = parseFloat(amtInput.value || "0") || 0;
          const r = parseFloat(rebateInput?.value || "0") || 0;
          const o = parseFloat(otherInput?.value || "0") || 0;
          netInput.value = (a - r + o).toFixed(2);
        }
      });

      document.querySelectorAll<HTMLInputElement | HTMLOutputElement>("[data-kit-sum-of]").forEach((out) => {
        const col = out.dataset.kitSumOf!;
        const column = `[data-kit-col='${col}']`;
        // Normal vouchers have a manually entered amount; an empty allocation grid still has its column in the template.
        if (!body.querySelector(column) && !tpl?.content.querySelector(column)) return;
        let total = 0;
        body.querySelectorAll("tr").forEach((row) => {
          const inp = row.querySelector<HTMLInputElement>(column);
          if (inp) total += parseFloat((inp.value || "0").replace(/,/g, "")) || 0;
        });
        if (out instanceof HTMLInputElement) {
          out.value = total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        } else {
          out.textContent = total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
      });

      const gdnNet = document.getElementById("gdn-total-net");
      if (gdnNet) {
        let totAmt = 0, totReb = 0, totOth = 0, totNet = 0;
        body.querySelectorAll("tr").forEach((row) => {
          const amtInput = row.querySelector<HTMLInputElement>("[data-kit-col='amount']");
          const rebateInput = row.querySelector<HTMLInputElement>("[data-kit-col='rebate']");
          const otherInput = row.querySelector<HTMLInputElement>("[data-kit-col='otherCharge']");
          const netInput = row.querySelector<HTMLInputElement>("[data-kit-col='net']");
          if (amtInput) {
            const a = parseFloat(amtInput.value || "0") || 0;
            const r = parseFloat(rebateInput?.value || "0") || 0;
            const o = parseFloat(otherInput?.value || "0") || 0;
            const n = parseFloat(netInput?.value || "0") || (a - r + o);
            totAmt += a; totReb += r; totOth += o; totNet += n;
          }
        });
        const gdnAmt = document.getElementById("gdn-total-amount");
        const gdnReb = document.getElementById("gdn-total-rebate");
        const gdnOth = document.getElementById("gdn-total-other");
        const gdnNet = document.getElementById("gdn-total-net");
        if (gdnAmt) gdnAmt.textContent = totAmt.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (gdnReb) gdnReb.textContent = totReb.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (gdnOth) gdnOth.textContent = totOth.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (gdnNet) gdnNet.textContent = totNet.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      const gpmrNet = document.getElementById("gpmr-total-net");
      if (gpmrNet) {
        let gpmrTotNet = 0;
        body.querySelectorAll("tr").forEach((row) => {
          const netInput = row.querySelector<HTMLInputElement>("[data-kit-col='net']");
          if (netInput) {
            const n = parseFloat(netInput.value || "0") || 0;
            gpmrTotNet += n;
          }
        });
        gpmrNet.textContent = gpmrTotNet.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      const sumConfig = grid.dataset.kitSum;
      if (!sumConfig) return;
      const [amtCol, typeCol] = sumConfig.split(":");
      let dr = 0;
      let cr = 0;
      body.querySelectorAll("tr").forEach((row) => {
        const amtInput = row.querySelector<HTMLInputElement>(`[data-kit-col='${amtCol}']`);
        const typeInput = row.querySelector<HTMLInputElement>(`[data-kit-col='${typeCol}']`);
        const amt = parseFloat(amtInput?.value || "0") || 0;
        const type = (typeInput?.value || "").trim().toLowerCase();
        if (type === "dr") dr += amt;
        else if (type === "cr") cr += amt;
      });
      const drOut = grid.querySelector<HTMLOutputElement>("[data-kit-sum-dr]");
      const crOut = grid.querySelector<HTMLOutputElement>("[data-kit-sum-cr]");
      const diffOut = grid.querySelector<HTMLOutputElement>("[data-kit-sum-diff]");
      if (drOut) drOut.textContent = dr.toFixed(2);
      if (crOut) crOut.textContent = cr.toFixed(2);
      if (diffOut) diffOut.textContent = Math.abs(dr - cr).toFixed(2);
    };

    const reindex = () => {
      Array.from(body.rows).forEach((row, idx) => {
        row.querySelectorAll<HTMLInputElement>("[name]").forEach((input) => {
          input.name = input.name.replace(/\[(#|\d+)\]/, `[${idx}]`);
        });
        row.querySelectorAll<HTMLInputElement>("[data-kit-ac-target]").forEach((input) => {
          if (input.dataset.kitAcTarget) {
            input.dataset.kitAcTarget = input.dataset.kitAcTarget.replace(/\[(#|\d+)\]/, `[${idx}]`);
          }
        });
        row.querySelectorAll<HTMLElement>("*").forEach((el) => {
          for (const attr of Array.from(el.attributes)) {
            if (attr.name.startsWith("data-kit-ac-param-")) {
              el.setAttribute(attr.name, attr.value.replace(/\[(#|\d+)\]/, `[${idx}]`));
            }
          }
        });
      });
      updateSums();
    };

    grid.addEventListener("focus", () => {
      const firstCell = grid.querySelector<HTMLInputElement>("tbody tr [data-kit-col]:not([readonly]):not([disabled]):not([tabindex='-1'])");
      if (firstCell && document.activeElement === grid) focusField(firstCell);
    });

    const addRow = (before?: HTMLTableRowElement, focus = true) => {
      const prevRow = before
        ? (before.previousElementSibling as HTMLTableRowElement | null)
        : (body.rows.length > 0 ? (body.rows[body.rows.length - 1] as HTMLTableRowElement | null) : null);

      if (!tpl) throw new Error("Cannot add a grid row without a row template");
      const tr = tpl.content.firstElementChild!.cloneNode(true) as HTMLTableRowElement;
      tr.querySelectorAll<HTMLElement>("[data-kit-bound], [data-kit-owns-enter]").forEach(el => {
        delete el.dataset.kitBound;
        delete el.dataset.kitOwnsEnter;
      });
      if (prevRow) {
        tr.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-kit-col]").forEach((cell) => {
          const col = cell.dataset.kitCol;
          const prevCell = prevRow.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-kit-col='${col}']`);
          if (prevCell && prevCell.value) {
            if (cell.dataset.kitDefaultFrom === "prev+1" || col === "mrNo") {
              const prevNum = parseInt(prevCell.value, 10);
              if (!isNaN(prevNum)) {
                cell.value = String(prevNum + 1);
              }
            } else if (cell.hasAttribute("data-kit-carry") || cell.dataset.kitDefaultFrom === "prev" || col === "mrSeriesId") {
              cell.value = prevCell.value;
            }
          }
        });
      }

      before ? body.insertBefore(tr, before) : body.appendChild(tr);
      reindex();
      run(tr);
      if (focus) focusField(cells(tr)[0]);
      return tr;
    };
    if (body.rows.length === 0) addRow(undefined, false);
    reindex();
    const isEmpty = (tr: HTMLTableRowElement) => {
      const biltiNo = tr.querySelector<HTMLInputElement>("[data-kit-col='biltiNo'], [data-kit-col='cnNo'], [data-kit-lookup-key]");
      if (biltiNo) return !biltiNo.value || biltiNo.value.trim() === "" || biltiNo.value === "0";

      const ac = tr.querySelector<HTMLInputElement>("[data-kit-ac]");
      if (ac) return !ac.value || ac.value.trim() === "";

      const amt = tr.querySelector<HTMLInputElement>("[data-kit-col='amount'], [data-kit-col='net']");
      if (amt) return !amt.value || amt.value.trim() === "" || amt.value === "0" || amt.value === "0.00";

      return !Array.from(tr.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-kit-col]"))
        .some((field) => {
          if (field.dataset.kitCol === "tax" || field.dataset.kitCol === "series" || field.dataset.kitCol === "mrSeriesId" || field.dataset.kitCol === "mrNo") return false;
          if (field.hasAttribute("data-kit-carry")) return false;
          if (field instanceof HTMLInputElement && (field.type === "checkbox" || field.type === "radio")) return false;
          return field.value.trim() !== "" && field.value !== "0" && field.value !== "0.00";
        });
    };
    const leave = () => {
      const after = grid.closest("form")?.querySelector<HTMLElement>("[data-kit-after-grid]");
      if (after) focusField(after); else moveFocus(grid, 1);
    };
    const setError = (tr: HTMLTableRowElement, cls: "" | "row-error" | "row-warn", msg = "") => {
      tr.classList.remove("row-error", "row-warn"); if (cls) tr.classList.add(cls);
      let err = tr.querySelector<HTMLElement>(".field-error");
      if (!err) { err = document.createElement("span"); err.className = "field-error"; tr.lastElementChild?.append(err); }
      err.textContent = msg; err.hidden = !msg;
    };

    type Reference = { seriesId: string; series: string; no: number; fullNo: string };
    const referenceUrl = grid.dataset.kitReferenceSearch;
    const referenceList = document.createElement("ul");
    referenceList.className = "kit-ac-list";
    referenceList.id = `kit-document-references-${++referenceListId}`;
    referenceList.setAttribute("role", "listbox");
    referenceList.setAttribute("aria-label", "Matching documents");
    referenceList.hidden = true;
    let referenceCell: HTMLInputElement | null = null;
    let references: Reference[] = [];
    let referenceSelected = 0;
    let referenceTimer = 0;
    let referenceSeq = 0;
    const closeReferences = () => {
      window.clearTimeout(referenceTimer);
      referenceSeq++;
      references = [];
      referenceList.hidden = true;
      referenceCell?.setAttribute("aria-expanded", "false");
      referenceCell?.removeAttribute("aria-activedescendant");
    };
    const chooseReference = (reference: Reference) => {
      const cell = referenceCell;
      closeReferences();
      if (!cell) return;
      cell.value = reference.fullNo;
      // Selection follows the same detail lookup, duplicate and eligibility checks as an exact typed number.
      cell.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true, cancelable: true }));
    };
    const renderReferences = (message?: string) => {
      referenceList.replaceChildren(...references.map((reference, index) => {
        const option = document.createElement("li");
        option.id = `${referenceList.id}-${index}`;
        option.setAttribute("role", "option");
        option.setAttribute("aria-selected", String(index === referenceSelected));
        option.textContent = reference.fullNo;
        option.addEventListener("mousedown", (event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          chooseReference(reference);
        });
        return option;
      }));
      if (message) {
        const status = document.createElement("li");
        status.setAttribute("role", "status");
        status.textContent = message;
        referenceList.append(status);
      }
      referenceList.hidden = references.length === 0 && !message;
      referenceCell?.setAttribute("aria-expanded", String(!referenceList.hidden));
      if (references.length) referenceCell?.setAttribute("aria-activedescendant", `${referenceList.id}-${referenceSelected}`);
    };
    const suggestReferences = (cell: HTMLInputElement) => {
      if (!referenceUrl) return;
      closeReferences();
      referenceCell = cell;
      cell.setAttribute("role", "combobox");
      cell.setAttribute("aria-autocomplete", "list");
      cell.setAttribute("aria-controls", referenceList.id);
      cell.after(referenceList);
      const value = cell.value.trim();
      if (!value) return;
      const seq = referenceSeq;
      referenceTimer = window.setTimeout(async () => {
        const form = grid.closest("form");
        const branch = form?.querySelector<HTMLInputElement | HTMLSelectElement>("[name=branchId]")?.value ?? "";
        const url = new URL(referenceUrl, location.origin);
        url.searchParams.set("branch_id", branch);
        url.searchParams.set("q", value);
        renderReferences("Searching documents…");
        try {
          const response = await fetch(url);
          if (seq !== referenceSeq || !cell.isConnected || document.activeElement !== cell || cell.value.trim() !== value) return;
          if (!response.ok) { renderReferences("Could not find documents. Press Enter to retry the number."); return; }
          const matches = await response.json() as Reference[];
          if (seq !== referenceSeq || !cell.isConnected || document.activeElement !== cell || cell.value.trim() !== value) return;
          references = matches;
          referenceSelected = 0;
          renderReferences(references.length ? undefined : "No matching documents. Check the number or series.");
        } catch {
          if (seq === referenceSeq) renderReferences("Could not find documents. Press Enter to retry the number.");
        }
      }, 100);
    };
    grid.addEventListener("focusout", (event) => { if (event.target === referenceCell) closeReferences(); });

    grid.addEventListener("input", (e) => {
      const field = e.target;
      if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) field.dataset.kitEdited = "1";
      // A retyped number or series makes the last lookup's fill (hidden id included) stale: clear it so a save never stores the old document.
      if (field instanceof HTMLElement && field.hasAttribute("data-kit-lookup-key")) {
        const tr = field.closest("tr");
        tr?.querySelectorAll<HTMLInputElement>("[data-kit-fill]").forEach((c) => { if (c !== field) c.value = ""; });
        // B6-07: the series came from the number, so a new number finds its own.
        if (grid.dataset.kitSeriesSearch) tr?.querySelectorAll<HTMLInputElement>("[data-kit-col='series'], [data-kit-col='cnSeries'], [data-kit-series-id]").forEach((c) => { c.value = ""; });
        if (tr) delete tr.dataset.warnAccepted;
        if (field instanceof HTMLInputElement) suggestReferences(field);
      }
      updateSums();
    });
    grid.addEventListener("change", updateSums);

    grid.addEventListener("keydown", async (e: KeyboardEvent) => {
      const cell = e.target as HTMLInputElement;
      if (cell === referenceCell && !referenceList.hidden) {
        if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); closeReferences(); return; }
        if (references.length && ["ArrowDown", "ArrowUp", "Enter", "Tab"].includes(e.key) && !e.shiftKey) {
          e.preventDefault(); e.stopPropagation();
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            referenceSelected = (referenceSelected + (e.key === "ArrowDown" ? 1 : references.length - 1)) % references.length;
            renderReferences();
          } else chooseReference(references[referenceSelected]!);
          return;
        }
      }
      if (cell === referenceCell && (e.key === "Enter" || e.key === "Tab")) closeReferences();
      const buffered = pendingLookups.get(cell);
      // Save (Ctrl+S / F10) waits too: fired at once it would save before the rows typed ahead are filled in.
      const save = e.key === "F10" || (e.ctrlKey && (e.key === "s" || e.key === "S"));
      if (buffered && (save || e.key === "Enter" || e.key === "Tab" || e.key === "Backspace" || e.key === "Delete" || (!e.ctrlKey && !e.altKey && !e.metaKey && e.key.length === 1))) {
        e.preventDefault(); e.stopPropagation();
        buffered.push({ key: e.key, shiftKey: e.shiftKey, ctrlKey: e.ctrlKey });
        return;
      }
      const tr = cell.closest("tr") as HTMLTableRowElement | null;
      if (!tr) return;
      const row = cells(tr); const i = row.indexOf(cell);
      if (e.ctrlKey && e.key === "Delete") { e.preventDefault(); const next = tr.nextElementSibling as HTMLTableRowElement | null; tr.remove(); reindex(); focusField(cells(next ?? body.rows[body.rows.length - 1] ?? addRow())[0]); return; } // reindex: a gap in Lines[n] made the server drop every later row
      if (e.ctrlKey && e.key === "Insert") { e.preventDefault(); addRow(tr); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const other = (e.key === "ArrowDown" ? tr.nextElementSibling : tr.previousElementSibling) as HTMLTableRowElement | null;
        if (other) focusField(other.querySelector<HTMLInputElement>(`[data-kit-col='${cell.dataset.kitCol}']`) ?? undefined);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault(); e.stopPropagation();
        if (cell.matches("[data-kit-ac]") && cell.dataset.kitOwnsEnter === "1") return;
        if (e.shiftKey) { focusField(row[i - 1]); return; }
        // An empty first box on a new row removes it and leaves the grid (CargoBooks: an empty C.Note goes to Remarks).
        if (i === 0 && isEmpty(tr) && body.rows.length > 1 && (!cell.value.trim() || cell.dataset.kitEdited !== "1")) { tr.remove(); reindex(); leave(); return; }
        if (cell.hasAttribute("data-kit-lookup-key")) {
          const seriesInput = tr.querySelector<HTMLInputElement>("[data-kit-col='series'], [data-kit-col='cnSeries']");
          if (cell.value.includes("/")) {
            const parts = cell.value.split("/");
            if (seriesInput) seriesInput.value = (parts[0] ?? "").trim();
            cell.value = (parts[1] ?? "").replace(/^0+/, "") || "0";
          }
          const no = cell.value.trim();
          if (!no) { if (body.rows.length > 1) { tr.remove(); reindex(); } leave(); return; }
          // B6-07: the row series fills itself from the number (data-kit-series-search names the document kind), so a
          // repeat is checked once the series is known; other grids check the number alone, as before.
          const search = seriesInput ? grid.dataset.kitSeriesSearch : undefined;
          const rowKey = (r: Element, series?: string) =>
            `${series ?? r.querySelector<HTMLInputElement>("[data-kit-col='series'], [data-kit-col='cnSeries']")?.value ?? ""}/${r.querySelector<HTMLInputElement>("[data-kit-lookup-key]")?.value.trim()}`.toLowerCase();
          const inGrid = (series?: string) => Array.from(body.rows).some((r) => r !== tr && (search ? rowKey(r) === rowKey(tr, series) : r.querySelector<HTMLInputElement>("[data-kit-lookup-key]")?.value.trim() === no));
          if (!search && inGrid()) { setError(tr, "row-error", `C.N. ${no} is already in this grid`); cell.select(); return; }
          const currentRow = cells(tr);
          const nextTarget = currentRow[currentRow.indexOf(cell) + 1];

          if (tr.dataset.warnAccepted !== no) {
            const form = grid.closest("form");
            const branchId = form?.querySelector<HTMLInputElement | HTMLSelectElement>("[name='branchId'], [name='Form.BranchId'], [name='BranchId']")?.value ?? "";
            const sep = (grid.dataset.kitLookup ?? "").includes("?") ? "&" : "?";
            // data-kit-lookup-include="to_branch_id,miti": extra header fields the lookup needs, sent under their own names.
            const extra = (grid.dataset.kitLookupInclude ?? "").split(",").map((n) => n.trim()).filter((n) => n.length > 0)
              .map((n) => `&${encodeURIComponent(n)}=${encodeURIComponent(form?.querySelector<HTMLInputElement | HTMLSelectElement>(`[name='${n}']`)?.value ?? "")}`).join("");
            type Looked = { series?: SeriesHit; ok: boolean; data: LookupResponse };
            const look = async (series: string, seriesId?: string): Promise<Looked> => {
              const param = grid.dataset.kitLookupSeriesParam || "series";
              const value = param === "series_id" ? (seriesId ?? series) : series;
              const r = await fetch(`${grid.dataset.kitLookup}${sep}branch_id=${encodeURIComponent(branchId)}&${param}=${encodeURIComponent(value)}&no=${encodeURIComponent(no)}${extra}`);
              return { ok: r.ok, data: await r.json() as LookupResponse };
            };
            // null = usable (a warning still is); otherwise the message to show.
            const refusal = (l: Looked) => {
              if (!l.ok || l.data.ok === false) return l.data.message ?? "Not found";
              const el = l.data.eligibility ?? { ok: true };
              // Two shapes: { ok: false, rule: "warn" } (row lookups) and { ok: true, level: "Warn" } (the bilti lookup).
              const warn = el.rule === "warn" || (el.level ?? "").toLowerCase() === "warn";
              return !el.ok && !warn ? (el.message ?? "Not found") : null;
            };
            const keys: BufferedKey[] = [];
            pendingLookups.set(cell, keys);
            let usable: Looked[] = [];
            let failure: string | null = null;
            try {
              if (!search) {
                const l = await look(seriesInput?.value ?? "");
                failure = refusal(l);
                usable = [l];
              } else {
                const typed = seriesInput!.value.trim().toLowerCase();
                const hits = await (await fetch(`/api/v1/series/by-number?doc=${encodeURIComponent(search)}&no=${encodeURIComponent(no)}`)).json() as { items: SeriesHit[]; message: string };
                const named = hits.items.filter((c) => !typed || c.name.toLowerCase() === typed);
                const candidates = named.filter((c) => !inGrid(c.name));
                failure = named.length > candidates.length ? `C.N. ${no} is already in this grid` : hits.message;
                // Own-branch screens (data-kit-own-series names the header series box) take the form's series first.
                const ownName = grid.dataset.kitOwnSeries;
                const own = ownName ? form?.querySelector<HTMLInputElement | HTMLSelectElement>(`[name='${ownName}']`)?.value : undefined;
                const first = candidates.find((c) => own && (c.id === own || c.name === own));
                if (first) {
                  const l = { ...await look(first.name, first.id), series: first };
                  const why = refusal(l);
                  if (why === null) usable = [l]; else failure = why;
                }
                // Otherwise (receiving screens too): every series with the number, kept where this screen accepts it.
                if (usable.length === 0) {
                  const rest = await Promise.all(candidates.filter((c) => c !== first).map(async (c) => ({ ...await look(c.name, c.id), series: c })));
                  usable = rest.filter((l) => refusal(l) === null);
                  if (!first && rest.length > 0) failure = refusal(rest[0]!) ?? failure;
                }
                if (usable.length > 0) failure = null;
              }
            } catch {
              failure = "Could not look up C.N. Try again.";
            } finally {
              pendingLookups.delete(cell);
            }
            // Paste or mouse edits can replace the number while keyboard input is held.
            // Never apply an older document's ID after the field changed or the grid was replaced.
            if (!cell.isConnected) return;
            if (cell.value.trim() !== no || (form?.querySelector<HTMLInputElement | HTMLSelectElement>("[name='branchId'], [name='Form.BranchId'], [name='BranchId']")?.value ?? "") !== branchId) {
              if (document.activeElement === cell) replay(keys);
              return;
            }
            if (failure !== null) { setError(tr, "row-error", failure); focusField(cell); return; }
            const apply = (l: Looked) => {
              if (l.series) {
                seriesInput!.value = l.series.name;
                tr.querySelectorAll<HTMLInputElement>("[data-kit-series-id]").forEach((c) => { c.value = l.series!.id; });
              }
              const data = l.data;
              tr.querySelectorAll<HTMLInputElement>("[data-kit-fill]").forEach((c) => {
                const v = data[c.dataset.kitFill!];
                if (v !== undefined) {
                  c.value = typeof v === "number" && (c.inputMode === "decimal" || c.dataset.kitFill === "amount" || c.dataset.kitCol === "net")
                    ? v.toFixed(2)
                    : String(v);
                }
              });
              updateSums();
              tr.dispatchEvent(new CustomEvent("kit:row-filled", { bubbles: true, detail: data })); // header boxes may default from the row (row-default.ts)
              const el = data.eligibility ?? { ok: true };
              if ((el.rule === "warn" || (el.level ?? "").toLowerCase() === "warn") && el.message) { setError(tr, "row-warn", el.message); tr.dataset.warnAccepted = no; focusField(cell); return; }
              setError(tr, "");
              if (nextTarget) focusField(nextTarget);
              replay(keys);
            };
            if (usable.length === 1) apply(usable[0]!);
            else pickSeries(cell, usable, apply);
            return;
          }
        }
        // The last cell of a row above others (an edit, copied rows) goes on to the next row, not to a new row at the bottom.
        if (i === row.length - 1) { const next = tr.nextElementSibling as HTMLTableRowElement | null; if (next) focusField(cells(next)[0]); else if (tpl) addRow(); else leave(); return; }
        focusField(row[i + 1]);
      }
    });

    /** B6-07: one number in several series: a small list by the number box; ↑/↓ move, Enter picks, Esc goes back to the number. */
    const pickSeries = <T extends { series?: SeriesHit }>(cell: HTMLInputElement, options: T[], pick: (o: T) => void) => {
      const list = document.createElement("ul"); list.className = "kit-ac-list"; list.setAttribute("role", "listbox"); list.setAttribute("aria-label", "Pick the series");
      let sel = 0;
      const close = () => { cell.removeEventListener("keydown", onKey); cell.removeEventListener("blur", close); list.remove(); };
      const render = () => list.replaceChildren(...options.map((o, n) => {
        const li = document.createElement("li"); li.setAttribute("role", "option"); li.setAttribute("aria-selected", String(n === sel));
        li.textContent = `${o.series?.name}/${cell.value}`;
        li.addEventListener("mousedown", (ev) => { ev.preventDefault(); close(); pick(o); });
        return li;
      }));
      function onKey(ev: KeyboardEvent) {
        if (!["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(ev.key)) return;
        ev.preventDefault(); ev.stopImmediatePropagation();
        if (ev.key === "ArrowDown" || ev.key === "ArrowUp") { sel = (sel + (ev.key === "ArrowDown" ? 1 : options.length - 1)) % options.length; render(); return; }
        close();
        if (ev.key === "Escape") cell.select(); else pick(options[sel]!);
      }
      cell.addEventListener("keydown", onKey);
      cell.addEventListener("blur", close);
      render();
      cell.after(list);
      focusField(cell);
    };

    grid.addEventListener("kit:enter", (e: Event) => {
      const cell = e.target as HTMLInputElement;
      const tr = cell.closest("tr") as HTMLTableRowElement | null;
      if (!tr) return;
      const row = cells(tr); const i = row.indexOf(cell);
      if (i === 0 && !cell.value.trim() && body.rows.length > 1) { tr.remove(); reindex(); leave(); return; }
      if (i === row.length - 1) { addRow(); return; }
      focusField(row[i + 1]);
    });

    // An Add-row button belongs to the grid in its own form section; a button elsewhere in the form (e.g. the footer)
    // only when the form has just this one grid, so one click never adds rows to two grids.
    const section = grid.closest(".cb-form-section");
    const local = section ? Array.from(section.querySelectorAll<HTMLElement>("[data-kit-grid-add]")) : [];
    const form = grid.closest("form");
    const addButtons = local.length > 0 ? local
      : form && form.querySelectorAll("table[data-kit-grid]").length === 1 ? Array.from(form.querySelectorAll<HTMLElement>("[data-kit-grid-add]")) : [];
    addButtons.forEach(btn => {
      if (btn.dataset.kitBound === "1") return;
      btn.dataset.kitBound = "1";
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        addRow();
      });
    });

    grid.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const deleteBtn = target.closest<HTMLElement>("[data-kit-row-delete]");
      if (deleteBtn) {
        e.preventDefault();
        const tr = deleteBtn.closest("tr") as HTMLTableRowElement | null;
        if (tr && body.rows.length > 1) {
          tr.remove();
          reindex();
        } else if (tr) {
          // the last row stays as an empty row to type into
          tr.querySelectorAll<HTMLInputElement>("input").forEach((input) => { input.value = ""; });
          updateSums();
        }
      }
    });

    grid.closest("form")?.addEventListener("submit", () => {
      if (!grid.isConnected) return; // Against changes replace the grid; its old submit listener must not rewrite the current total.
      Array.from(body.rows).forEach((row) => {
        if (isEmpty(row)) row.remove();
      });
      reindex();
    });
  });
});
